import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MessageSquare, Truck, ExternalLink, Edit2, Save, X, MousePointerClick, Eye, Clock, StickyNote, Trash2, Plus, Mail, Loader2, Send, MailOpen, CheckCircle2, DollarSign, Contact, MessageCircle, ClipboardList, Link2, Copy, Phone } from 'lucide-react';
import EmailCompose from './EmailCompose';
import LocalInventoryMatches from './dashboard/LocalInventoryMatches';
import { getAuthToken } from '../utils/auth';
import { useTheme } from '../context/ThemeContext';
import { formatUsPhone, formatUsPhoneInput } from '../utils/phone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * LeadDetailModal - Displays full lead profile, quiz answers, and AI-matched RVs
 * 
 * Props:
 *   isOpen: boolean - Controls modal visibility
 *   onClose: function - Called when modal should close
 *   lead: object - The lead data to display
 *   onLeadUpdate: function - Optional callback when lead is updated
 */
const LeadDetailModal = ({ isOpen, onClose, lead, onLeadUpdate, onConvertToSale, onDelete }) => {
  const { isDark } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ name: '', email: '', phone: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleData, setScheduleData] = useState({ preferred_date: '', preferred_time: '', notes: '' });
  const [isScheduling, setIsScheduling] = useState(false);
  const [isGeneratingMatches, setIsGeneratingMatches] = useState(false);
  
  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [showEmailCompose, setShowEmailCompose] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);

  // Dealer cloaked links — dealer-added URLs from their dealership website
  // (specific RV listings, financing pages, trade-in forms) surfaced as
  // branded rvmatchai.app/{slug}/rv/{code} short links the buyer can click.
  const [dealerLinks, setDealerLinks] = useState([]);
  const [linkComposeSeed, setLinkComposeSeed] = useState(null);

  // Google Contacts export state — is this dealer connected to Google, and
  // has this specific lead already been pushed to their Google Contacts?
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleExported, setGoogleExported] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);

  // The button rendered "Connect Google" forever because googleConnected
  // was never fetched — check real status every time the modal opens.
  useEffect(() => {
    if (!isOpen || !lead?.id) return;
    const token = getAuthToken();
    const hdrs = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/google/status`, hdrs)
      .then((r) => setGoogleConnected(!!r.data?.connected))
      .catch(() => setGoogleConnected(false));
    axios.get(`${API}/leads/${lead.id}/google-contact/status`, hdrs)
      .then((r) => setGoogleExported(!!r.data?.exported))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lead?.id]);

  const fetchEmailHistory = async () => {
    if (!lead?.id) return;
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/lead/${lead.id}/emails`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmailHistory(res.data.emails || []);
    } catch (err) {
      console.error('Error fetching email history:', err);
    }
  };

  const fetchDealerLinks = async () => {
    if (!lead?.id) return;
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/leads/${lead.id}/links`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDealerLinks(res.data.links || []);
      // Clear the red-dot on view (best-effort, non-blocking)
      axios
        .post(`${API}/dealer/leads/${lead.id}/links/mark-viewed`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch(() => { /* noop */ });
    } catch (err) {
      console.error('Error fetching dealer links:', err);
    }
  };

  const handleCreateDealerLink = async ({ title, target_url, notes: linkNotes }) => {
    if (!lead?.id) return null;
    try {
      const token = getAuthToken();
      const res = await axios.post(
        `${API}/dealer/leads/${lead.id}/links`,
        { title, target_url, notes: linkNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDealerLinks((prev) => [res.data, ...prev]);
      toast.success('Link added — copy the branded URL to share.');
      return res.data;
    } catch (err) {
      console.error('Error creating dealer link:', err);
      toast.error(err.response?.data?.detail || 'Failed to create link');
      return null;
    }
  };

  const handleDeleteDealerLink = async (linkId) => {
    if (!lead?.id) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/dealer/leads/${lead.id}/links/${linkId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDealerLinks((prev) => prev.filter((l) => l.id !== linkId));
      toast.success('Link removed');
    } catch (err) {
      console.error('Error deleting dealer link:', err);
      toast.error('Failed to delete link');
    }
  };

  const handleSendLinkInEmail = (link) => {
    // Prime the email composer with a subject + body containing the cloaked URL
    setLinkComposeSeed({
      subject: `Check out this RV — ${link.title}`,
      body: `Hi ${lead?.first_name || 'there'},\n\nI thought this one might be a great fit for you:\n\n${link.title}\n${link.cloaked_url}\n\nLet me know what you think!\n`,
    });
    setShowEmailCompose(true);
  };

  // Bulk-send handler — when the dealer selects multiple links and clicks
  // "Send as Local Matches" we drop them into a nicely-formatted roundup
  // email pre-filled in the Email Center compose window.
  const handleSendLocalMatches = (selectedLinks) => {
    if (!selectedLinks?.length) return;
    const first = lead?.first_name || (lead?.name || '').split(' ')[0] || 'there';
    const rvTypeBlurb = lead?.rv_type && lead.rv_type !== 'Not specified'
      ? ` for ${lead.rv_type.toLowerCase()}s`
      : '';
    const listing = selectedLinks
      .map((l, i) => `${i + 1}. ${l.title}\n   ${l.cloaked_url}`)
      .join('\n\n');
    setLinkComposeSeed({
      subject: `${selectedLinks.length} local RV match${selectedLinks.length === 1 ? '' : 'es'} from our lot`,
      body:
`Hi ${first},

Great news — I pulled ${selectedLinks.length} local match${selectedLinks.length === 1 ? '' : 'es'}${rvTypeBlurb} for you from our inventory. Tap any of the links below to see the full details:

${listing}

Reply with which one grabs your attention and I'll get you all the numbers, availability, and set up a time for you to walk through it.

Talk soon!
`,
    });
    setShowEmailCompose(true);
  };

  useEffect(() => {
    if (lead && isOpen) {
      fetchEmailHistory();
      fetchDealerLinks();
    }
  }, [lead, isOpen]);

  const handleResendNotification = async () => {
    if (!lead?.id || isResending) return;
    setIsResending(true);
    try {
      const token = getAuthToken();
      await axios.post(
        `${API}/dealer/lead/${lead.id}/resend-notification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notification email resent successfully');
    } catch (error) {
      console.error('Error resending notification:', error);
      toast.error('Failed to resend notification');
    } finally {
      setIsResending(false);
    }
  };

  // Reset edit state when lead changes or modal closes
  useEffect(() => {
    if (lead) {
      setEditData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || ''
      });
      setNotes(lead.notes || []);
    }
    setIsEditing(false);
    setIsAddingNote(false);
    setNewNote('');
  }, [lead, isOpen]);

  if (!lead) return null;

  const budgetMax = lead.matching_criteria?.budget_max || 0;

  const handleSaveContact = async () => {
    setIsSaving(true);
    try {
      const token = getAuthToken();
      const digitsOnlyPhone = editData.phone ? editData.phone.replace(/\D/g, '').slice(-10) : '';

      const response = await axios.put(
        `${API}/dealer/lead/${lead.id}/contact`,
        { ...editData, phone: digitsOnlyPhone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Contact information updated');
      setIsEditing(false);
      
      // Notify parent component of the update
      if (onLeadUpdate) {
        onLeadUpdate(response.data);
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error(error.response?.data?.detail || 'Failed to update contact information');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData({
      name: lead.name || '',
      email: lead.email || '',
      phone: lead.phone || ''
    });
    setIsEditing(false);
  };

  const handleScheduleAppointment = async () => {
    if (!scheduleData.preferred_date || !scheduleData.preferred_time) {
      toast.error('Pick both a date and a time.');
      return;
    }
    setIsScheduling(true);
    try {
      const token = getAuthToken();
      await axios.post(
        `${API}/dealer/lead/${lead.id}/appointment`,
        scheduleData,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Appointment scheduled — visible in the Appointments tab');
      setShowScheduleForm(false);
      setScheduleData({ preferred_date: '', preferred_time: '', notes: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not schedule appointment');
    } finally {
      setIsScheduling(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setIsSavingNote(true);
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API}/dealer/lead/${lead.id}/notes`,
        { text: newNote.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotes(response.data.notes);
      setNewNote('');
      setIsAddingNote(false);
      toast.success('Note added');
      
      if (onLeadUpdate) {
        onLeadUpdate({ ...lead, notes: response.data.notes });
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const token = getAuthToken();
      const response = await axios.delete(
        `${API}/dealer/lead/${lead.id}/notes/${noteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotes(response.data.notes);
      toast.success('Note deleted');
      
      if (onLeadUpdate) {
        onLeadUpdate({ ...lead, notes: response.data.notes });
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handleGenerateMoreMatches = async () => {
    if (!lead?.session_id) {
      toast.error('Cannot generate matches - no session ID');
      return;
    }
    
    setIsGeneratingMatches(true);
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API}/dealer/lead/${lead.id}/regenerate-matches`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('New RV matches generated and sent to lead!');
      
      // Update the lead with new matches
      if (onLeadUpdate && response.data.lead) {
        onLeadUpdate(response.data.lead);
      }
    } catch (error) {
      console.error('Error generating matches:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate new matches');
    } finally {
      setIsGeneratingMatches(false);
    }
  };

  const handleSaveToContacts = async () => {
    if (!lead?.id) return;
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/lead/${lead.id}/vcard`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/vcard' }));
      const safeName = (lead.name || 'lead').replace(/[^A-Za-z0-9_-]/g, '_');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName}.vcf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Contact downloaded — open the file to save it to your phone.');
    } catch (err) {
      toast.error('Could not generate vCard.');
    }
  };

  // The button rendered "Connect Google" forever because googleConnected
  // was never fetched — check real status every time the modal opens.
  const handleSaveToGoogleContacts = async () => {
    if (!lead?.id) return;
    setGoogleBusy(true);
    try {
      const token = getAuthToken();
      if (!googleConnected) {
        // Kick off OAuth. When Google redirects back the app URL will have
        // ?google=connected and this modal will re-fetch status on next open.
        const r = await axios.get(`${API}/google/connect`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.data?.authorization_url) {
          window.location.href = r.data.authorization_url;
          return;
        }
        toast.error('Could not start Google connect.');
        return;
      }
      const r = await axios.post(
        `${API}/leads/${lead.id}/google-contact`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.already_exported) {
        toast.info('Already saved to Google Contacts.');
      } else {
        toast.success('Saved to Google Contacts — check your phone.');
      }
      setGoogleExported(true);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Google save failed';
      if (err.response?.status === 409) {
        // Token expired — treat as disconnected so next click re-connects
        setGoogleConnected(false);
      }
      toast.error(detail);
    } finally {
      setGoogleBusy(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-4xl max-h-[90vh] overflow-y-auto ${isDark ? 'bg-gray-900 text-white border-gray-700' : ''}`} data-testid="lead-detail-modal">
        <DialogHeader className="border-b pb-3 mb-0">
          {/* Title row — clean, no buttons crammed into it */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <DialogTitle
                className={`text-xl sm:text-2xl font-bold leading-tight break-words ${isDark ? 'text-white' : 'text-[#004E86]'}`}
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                {(lead?.name && lead.name.trim()) || lead?.email || 'Lead Profile'}
              </DialogTitle>
              {/* Sub-line: RV type · budget · badges.
                  Each fact is wrapped in a single non-breaking span (with a
                  leading middle-dot separator on all but the first) so
                  multi-word values like "Travel Trailer" never split across
                  lines and dots never orphan onto their own line on mobile. */}
              <div className={`mt-1 flex items-center flex-wrap gap-x-2 gap-y-1 text-sm ${isDark ? 'text-blue-300' : 'text-[#004E86]/80'}`}>
                {lead?.rv_type && lead.rv_type !== 'Not specified' && (
                  <span className="font-medium whitespace-nowrap">{lead.rv_type}</span>
                )}
                {lead?.matching_criteria?.budget_max && (
                  <span className="whitespace-nowrap">
                    <span className="text-gray-400 mr-2" aria-hidden>·</span>
                    up to ${lead.matching_criteria.budget_max.toLocaleString()}
                  </span>
                )}
                {lead?.zipcode && (
                  <span className="inline-flex items-center whitespace-nowrap">
                    <span className="text-gray-400 mr-2" aria-hidden>·</span>
                    <LocationChip zip={lead.zipcode} />
                  </span>
                )}
                <SourceBadges lead={lead} />
              </div>
            </div>
          </div>

          {/* Actions row — wraps naturally, grouped by intent.
              Buttons scale up on mobile (h-10) for comfortable tap targets;
              stay compact (h-8) on desktop where cursors are precise. */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {/* Save-contact group */}
            <Button
              size="sm"
              variant="outline"
              onClick={handleSaveToContacts}
              className="flex items-center gap-1.5 h-10 sm:h-8 px-3 text-xs bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700"
              data-testid="save-vcard-btn"
              title="Download a vCard to save this contact into your phone"
            >
              <Contact size={14} />
              vCard
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSaveToGoogleContacts}
              disabled={googleBusy}
              className={`flex items-center gap-1.5 h-10 sm:h-8 px-3 text-xs ${
                googleExported
                  ? 'bg-green-50 hover:bg-green-100 border-green-200 text-green-700'
                  : googleConnected
                    ? 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700'
                    : 'bg-red-50 hover:bg-red-100 border-red-200 text-red-700'
              }`}
              data-testid="save-google-contact-btn"
              title={
                googleExported
                  ? 'Already saved to Google Contacts'
                  : googleConnected
                    ? 'Push this lead to your Google Contacts'
                    : 'Connect your Google account, then save'
              }
            >
              {googleBusy ? (
                <Loader2 size={14} className="animate-spin" />
              ) : googleExported ? (
                <CheckCircle2 size={14} />
              ) : (
                <Contact size={14} />
              )}
              {googleExported ? 'Saved' : googleConnected ? 'Google' : 'Connect Google'}
            </Button>

            {/* Divider (hidden on tight mobile wraps) */}
            <span className="hidden sm:inline-block w-px h-6 bg-gray-200 dark:bg-gray-700" />

            {/* Email quick-action opens inline composer below */}
            <Button
              size="sm"
              onClick={() => setShowEmailCompose((s) => !s)}
              className="flex items-center gap-1.5 h-10 sm:h-8 px-3 text-xs bg-[#004E86] hover:bg-[#003a66] text-white"
              data-testid="quick-email-btn"
            >
              <Mail size={14} />
              {showEmailCompose ? 'Close Email' : 'Email Lead'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleResendNotification}
              disabled={isResending}
              className="flex items-center gap-1.5 h-10 sm:h-8 px-3 text-xs"
              data-testid="resend-notification-btn"
            >
              {isResending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              Resend Matches
            </Button>
              <Button
              size="sm"
              variant="outline"
              onClick={() => setShowScheduleForm(true)}
              className="flex items-center gap-1.5 h-10 sm:h-8 px-3 text-xs border-[#004E86] text-[#004E86] hover:bg-[#004E86] hover:text-white"
              data-testid="schedule-appointment-btn"
            >
              <Phone size={14} />
              Schedule Appointment
            </Button>

            {/* Divider (hidden on tight mobile wraps) */}
            <span className="hidden sm:inline-block w-px h-6 bg-gray-200 dark:bg-gray-700" />

            {onConvertToSale && (
              lead.status === 'qualified' ||
              lead.status === 'closed' ||
              lead.engagement?.level === 'hot' ||
              (typeof lead.intent_score === 'number' && lead.intent_score >= 80)
            ) && (
              <Button
                size="sm"
                onClick={() => { onClose(false); onConvertToSale(lead); }}
                className="flex items-center gap-1.5 h-10 sm:h-8 px-3 text-xs bg-green-600 hover:bg-green-700 text-white"
                data-testid="convert-to-sale-btn"
              >
                <DollarSign size={14} />
                Convert to Sale
              </Button>
            )}
            {onDelete && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-1.5 h-10 sm:h-8 px-3 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                data-testid="delete-lead-btn"
              >
                <Trash2 size={14} />
                Delete
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Schedule Appointment inline panel — renders when the header
            button is clicked. Uses lead's name/email/phone from record,
            just needs date/time from the dealer. Posts to /dealer/lead/
            {id}/appointment which lands in the Appointments tab. */}
        {showScheduleForm && (
          <div
            className={`mx-6 my-4 p-4 rounded-xl border ${isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-blue-50/50 border-blue-200'}`}
            data-testid="schedule-appointment-panel"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Schedule an appointment with {lead?.name || lead?.email}
              </h3>
              <button
                onClick={() => setShowScheduleForm(false)}
                className={`text-xs opacity-70 hover:opacity-100 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                data-testid="schedule-appointment-close"
              >
                Cancel
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Date
                <input
                  type="date"
                  value={scheduleData.preferred_date}
                  onChange={(e) => setScheduleData((s) => ({ ...s, preferred_date: e.target.value }))}
                  min={new Date().toISOString().slice(0, 10)}
                  className={`mt-1 w-full rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-slate-900 border border-slate-700 text-white' : 'bg-white border border-slate-300'}`}
                  data-testid="schedule-appointment-date"
                />
              </label>
              <label className={`text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Time
                <input
                  type="text"
                  placeholder="e.g. 2:00 PM CT"
                  value={scheduleData.preferred_time}
                  onChange={(e) => setScheduleData((s) => ({ ...s, preferred_time: e.target.value }))}
                  className={`mt-1 w-full rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-slate-900 border border-slate-700 text-white' : 'bg-white border border-slate-300'}`}
                  data-testid="schedule-appointment-time"
                />
              </label>
              <label className={`sm:col-span-2 text-xs ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                Notes (optional)
                <textarea
                  rows={2}
                  value={scheduleData.notes}
                  onChange={(e) => setScheduleData((s) => ({ ...s, notes: e.target.value }))}
                  placeholder="Test drive, financing walkthrough, walkaround, etc."
                  className={`mt-1 w-full rounded-lg px-3 py-2 text-sm ${isDark ? 'bg-slate-900 border border-slate-700 text-white' : 'bg-white border border-slate-300'}`}
                  data-testid="schedule-appointment-notes"
                />
              </label>
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button
                onClick={handleScheduleAppointment}
                disabled={isScheduling}
                className="bg-[#004E86] hover:bg-[#003a66] text-white h-9"
                data-testid="schedule-appointment-submit"
              >
                {isScheduling ? <Loader2 size={14} className="animate-spin mr-1" /> : <Phone size={14} className="mr-1" />}
                Confirm appointment
              </Button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
            <div className={`p-6 rounded-lg max-w-md ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-2">Delete Lead?</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Are you sure you want to delete <strong>{lead?.name || lead?.email}</strong>? 
                This will remove all their data and allow them to retake the quiz.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  data-testid="cancel-delete-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    onDelete(lead.id);
                  }}
                  data-testid="confirm-delete-btn"
                >
                  Delete Lead
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className={`space-y-6 ${isDark ? 'text-white' : ''}`}>
          {/* Contact Info Section */}
          <ContactInfoSection 
            lead={lead} 
            isEditing={isEditing}
            editData={editData}
            setEditData={setEditData}
            onEditClick={() => setIsEditing(true)}
            onSave={handleSaveContact}
            onCancel={handleCancelEdit}
            isSaving={isSaving}
          />

          {/* Tow Vehicle + Trade-In summary — pulled straight from the
              buyer's form submission. Only renders when at least one of
              tow / trade info is present so we don't waste space. */}
          <TowTradeCard lead={lead} isDark={isDark} />

          {/* Advisor Website Links — cloaked rvmatchai.app URLs the dealer
              can share with the buyer (specific RV listings, financing pages,
              trade-in forms). Every click is tracked. */}
          <DealerLinksSection
            isDark={isDark}
            links={dealerLinks}
            onCreate={handleCreateDealerLink}
            onDelete={handleDeleteDealerLink}
            onSendInEmail={handleSendLinkInEmail}
            onSendLocalMatches={handleSendLocalMatches}
            leadName={lead?.first_name || (lead?.name || '').split(' ')[0]}
          />

          {/* Notes Section - Yellow Box */}
          <NotesSection 
            notes={notes}
            newNote={newNote}
            setNewNote={setNewNote}
            isAddingNote={isAddingNote}
            setIsAddingNote={setIsAddingNote}
            isSavingNote={isSavingNote}
            onAddNote={handleAddNote}
            onDeleteNote={handleDeleteNote}
          />

          {/* Email Communication Section */}
          <EmailSection
            lead={lead}
            isDark={isDark}
            showCompose={showEmailCompose}
            setShowCompose={setShowEmailCompose}
            emailHistory={emailHistory}
            onEmailSent={fetchEmailHistory}
            preloadedTemplate={linkComposeSeed}
          />

          {/* Tracking Activity Section */}
          <TrackingActivitySection lead={lead} />

          {/* Ranger Chat Transcript — shown when this lead came from Ranger */}
          {lead.has_ranger_chat && lead.ranger_chat && (
            <RangerChatSection lead={lead} isDark={isDark} />
          )}

          {/* Quiz Answers Section */}
          {lead.user_responses && Object.keys(lead.user_responses).length > 0 && (
            <QuizResponsesSection responses={lead.user_responses} />
          )}

          {/* Matching Criteria Summary */}
          {lead.matching_criteria && (
            <MatchingCriteriaSection criteria={lead.matching_criteria} />
          )}

          {/* Customer-uploaded attachments — files (notes, spreadsheets,
              screenshots) the lead added on the application form. */}
          {lead.attachments && lead.attachments.length > 0 && (
            <LeadAttachmentsSection
              attachments={lead.attachments}
              sessionId={lead.session_id}
            />
          )}
          
          {/* Matched RVs Section with Generate More button */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wide flex items-center gap-2">
                <Truck size={16} />
                AI-Matched RVs ({lead.matched_rvs?.length || 0})
              </h3>
              <Button
                size="sm"
                onClick={handleGenerateMoreMatches}
                disabled={isGeneratingMatches}
                className="flex items-center gap-1.5 h-8 text-xs bg-[#004E86] hover:bg-[#003a66] text-white"
                data-testid="generate-more-matches-btn"
              >
                {isGeneratingMatches ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Plus size={14} />
                    Generate & Send New Matches
                  </>
                )}
              </Button>
            </div>
            {lead.matched_rvs && lead.matched_rvs.length > 0 ? (
              <MatchedRVsSection matches={lead.matched_rvs} budgetMax={budgetMax} />
            ) : (
              <NoMatchesSection />
            )}
          </div>

          {/* Local inventory matches — dealer-only view. Silent when no
              inventory has been uploaded. */}
          <LocalInventoryMatches leadId={lead.id} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Source Badges — Show which channels the lead came from (Quiz, Ranger, or both)
 */
const SourceBadges = ({ lead }) => {
  const hasQuiz = lead?.has_quiz ?? Boolean(lead?.user_responses && Object.keys(lead.user_responses).length > 0);
  const hasRanger = Boolean(lead?.has_ranger_chat);
  if (!hasQuiz && !hasRanger) return null;
  const both = hasQuiz && hasRanger;
  return (
    <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid="lead-source-badges">
      {both && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-blue-500 to-orange-500 text-white shadow"
          data-testid="lead-source-badge-both"
          title="Lead completed both the Form and a Ranger chat"
        >
          ★ Form + Ranger
        </span>
      )}
      {!both && hasQuiz && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-500/15 text-blue-600 border border-blue-500/30"
          data-testid="lead-source-badge-quiz"
        >
          <ClipboardList size={10} /> Form
        </span>
      )}
      {!both && hasRanger && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-orange-500/15 text-orange-600 border border-orange-500/30"
          data-testid="lead-source-badge-ranger"
        >
          <MessageCircle size={10} /> Ranger
        </span>
      )}
    </div>
  );
};

/**
 * Ranger Chat Section — surfaces the saved Ranger transcript inside the
 * unified lead profile. Only rendered when the lead has a Ranger chat block.
 */
const RangerChatSection = ({ lead, isDark }) => {
  const chat = lead?.ranger_chat;
  if (!chat) return null;
  const transcript = chat.transcript || [];
  return (
    <div
      className={`rounded-xl p-4 border ${isDark ? 'bg-orange-500/5 border-orange-500/20' : 'bg-orange-50 border-orange-200'}`}
      data-testid="ranger-chat-section"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-sm font-bold uppercase tracking-wide flex items-center gap-2 ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
          <MessageCircle size={16} />
          Ranger Chat Transcript
        </h3>
        <span className={`text-xs ${isDark ? 'text-orange-300/70' : 'text-orange-600'}`}>
          {chat.user_msg_count || 0} question{(chat.user_msg_count || 0) === 1 ? '' : 's'} • saved {chat.captured_at ? new Date(chat.captured_at).toLocaleDateString() : ''}
        </span>
      </div>
      {transcript.length === 0 ? (
        <p className={`text-xs italic ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No messages recorded.</p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {transcript.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-gradient-to-br from-orange-400 to-red-500 text-white rounded-tr-sm'
                    : isDark ? 'bg-gray-800 text-gray-100 rounded-tl-sm' : 'bg-white text-gray-800 rounded-tl-sm border border-orange-100'
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Contact Information Section with Edit functionality
 */
const ContactInfoSection = ({ lead, isEditing, editData, setEditData, onEditClick, onSave, onCancel, isSaving }) => (
  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-bold text-gray-500 dark:text-gray-300 dark:text-gray-300 uppercase tracking-wide">Contact Information</h3>
      {!isEditing ? (
        <Button
          size="sm"
          variant="outline"
          onClick={onEditClick}
          className="flex items-center gap-1 h-7 text-xs"
          data-testid="edit-contact-btn"
        >
          <Edit2 size={12} />
          Edit
        </Button>
      ) : (
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            className="flex items-center gap-1 h-7 text-xs"
            disabled={isSaving}
            data-testid="cancel-edit-btn"
          >
            <X size={12} />
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            className="flex items-center gap-1 h-7 text-xs bg-[#004E86] text-white hover:bg-[#003d6b]"
            disabled={isSaving}
            data-testid="save-contact-btn"
          >
            <Save size={12} />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}
    </div>
    
    {isEditing ? (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <label className="text-xs font-semibold text-gray-400 block mb-1">Name</label>
          <Input
            value={editData.name}
            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
            placeholder="Enter name"
            className="h-8 text-sm"
            data-testid="edit-name-input"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-400 block mb-1">Email</label>
          <Input
            type="email"
            value={editData.email}
            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
            placeholder="Enter email"
            className="h-8 text-sm"
            data-testid="edit-email-input"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-400 block mb-1">Phone</label>
          <Input
            value={editData.phone}
            onChange={(e) => setEditData({ ...editData, phone: formatUsPhoneInput(e.target.value) })}
            placeholder="Enter phone"
            className="h-8 text-sm"
            data-testid="edit-phone-input"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-400 block mb-1">Zipcode</label>
          <p className="text-sm font-medium mt-1">{lead.zipcode || 'Not provided'}</p>
        </div>
      </div>
    ) : (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <p className="text-xs font-semibold text-gray-400">Name</p>
          <p className="text-sm font-medium" data-testid="lead-name">{lead.name || 'Not provided'}</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-400">Email</p>
          <p className="text-sm font-medium break-all" data-testid="lead-email">{lead.email}</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-400">Phone</p>
          {lead.phone ? (
            <a
              href={`tel:${(lead.phone || '').replace(/[^0-9+]/g, '')}`}
              className="text-sm font-semibold text-emerald-600 hover:text-emerald-700 hover:underline inline-flex items-center gap-1"
              data-testid="lead-phone"
              title="Tap to call"
            >
              {formatUsPhone(lead.phone)}
              <span aria-hidden="true" className="text-emerald-500">📞</span>
            </a>
          ) : (
            <p className="text-sm font-medium" data-testid="lead-phone">Not provided</p>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-400">Location</p>
          {lead.zipcode ? (
            <p className="text-sm font-medium break-words">
              <LocationChip zip={lead.zipcode} />
              <span className="text-xs text-gray-400 ml-1">({lead.zipcode})</span>
            </p>
          ) : (
            <p className="text-sm font-medium">Not provided</p>
          )}
        </div>
      </div>
    )}
    
    <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t">
      <div>
        <p className="text-xs font-semibold text-gray-400">RV Type Wanted</p>
        <p className="text-sm font-medium text-[#004E86] dark:text-blue-300">
          {lead.rv_type || 'Not specified'}
          {lead.matching_criteria?.toy_hauler_style && lead.matching_criteria.toy_hauler_style !== 'either'
            ? ` (${lead.matching_criteria.toy_hauler_style.replace('_', ' ')})`
            : ''}
        </p>
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-400">Intent Score</p>
        <p className="text-sm font-bold text-[#F2A900]">{lead.intent_score}/100</p>
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-400">Date</p>
        <p className="text-sm font-medium">{new Date(lead.created_at).toLocaleDateString()}</p>
      </div>
    </div>
  </div>
);

/**
 * Notes Section - Yellow box for dealer notes
 */
/**
 * DealerLinksSection - Cloaked branded URLs the dealer attaches from their
 * dealership website (specific RV listings, financing pages, trade-in forms).
 * Each link generates a rvmatchai.app/{slug}/rv/{code} short URL the dealer
 * can copy or send to the buyer via the Email Center. Every click is tracked
 * and lights up an "unread click" dot on the lead.
 */
const DealerLinksSection = ({ isDark, links, onCreate, onDelete, onSendInEmail, onSendLocalMatches, leadName }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // Multi-select — dealer picks 2+ links and hits "Send as Local Matches" to
  // email them all to the lead in one branded roundup.
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(links.map(l => l.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const resetForm = () => {
    setTitle('');
    setUrl('');
    setIsAdding(false);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !url.trim()) {
      toast.error('Please add both a title and a URL');
      return;
    }
    setIsSaving(true);
    const created = await onCreate({ title: title.trim(), target_url: url.trim() });
    setIsSaving(false);
    if (created) resetForm();
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied cloaked link');
    } catch {
      // Fallback: create a temporary input, select, copy
      const tmp = document.createElement('input');
      tmp.value = text;
      document.body.appendChild(tmp);
      tmp.select();
      try { document.execCommand('copy'); toast.success('Copied cloaked link'); }
      catch { toast.error('Copy failed — long-press to copy manually'); }
      document.body.removeChild(tmp);
    }
  };

  return (
    <div className={`rounded-xl p-4 border ${isDark ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-indigo-50 border-indigo-100'}`} data-testid="dealer-links-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-sm font-bold uppercase tracking-wide flex items-center gap-2 ${isDark ? 'text-indigo-300' : 'text-indigo-800'}`}>
          <Link2 size={16} />
          Dealership Links ({links.length})
        </h3>
        {!isAdding && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-1 h-7 text-xs"
            data-testid="add-dealer-link-btn"
          >
            <Plus size={12} /> Add Link
          </Button>
        )}
      </div>

      {/* Add-link form */}
      {isAdding && (
        <div className={`mb-3 p-3 rounded-lg border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-indigo-200'}`}>
          <Input
            placeholder="Link title (e.g. 2024 Grand Design Imagine XLS)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mb-2 text-sm"
            data-testid="dealer-link-title-input"
          />
          <Input
            placeholder="https://your-dealership.com/inventory/rv-123"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="mb-2 text-sm"
            data-testid="dealer-link-url-input"
          />
          <div className="flex items-center gap-2 justify-end">
            <Button
              size="sm"
              variant="ghost"
              onClick={resetForm}
              className="text-xs h-7"
              data-testid="dealer-link-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={isSaving || !title.trim() || !url.trim()}
              className="flex items-center gap-1 text-xs h-7 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="dealer-link-save-btn"
            >
              {isSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save
            </Button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {links.length === 0 && !isAdding && (
        <p className={`text-xs ${isDark ? 'text-indigo-200/70' : 'text-indigo-700/80'}`}>
          Add links from your dealership website — they&apos;ll be cloaked as branded
          <span className="font-semibold"> rvmatchai.app</span> URLs so buyers see your matches, not raw dealer URLs.
        </p>
      )}

      {/* Bulk action bar — shows once dealer picks 2+ links, drops them all
          into one branded "Local Matches" email to the buyer. */}
      {links.length > 1 && (
        <div className={`mb-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2 ${
          selectedIds.size > 0
            ? (isDark ? 'bg-indigo-500/15 border border-indigo-400/30' : 'bg-indigo-100 border border-indigo-300')
            : (isDark ? 'bg-gray-800/40 border border-gray-700' : 'bg-white border border-indigo-100')
        }`}>
          <div className="flex items-center gap-2 text-xs">
            <button
              onClick={selectedIds.size === links.length ? clearSelection : selectAll}
              className={`underline ${isDark ? 'text-indigo-200' : 'text-indigo-700'}`}
              data-testid="dealer-links-select-all"
            >
              {selectedIds.size === links.length ? 'Clear' : 'Select all'}
            </button>
            <span className={isDark ? 'text-indigo-100/70' : 'text-indigo-800/80'}>
              {selectedIds.size} of {links.length} selected
            </span>
          </div>
          <Button
            size="sm"
            disabled={selectedIds.size === 0}
            onClick={() => {
              const picked = links.filter(l => selectedIds.has(l.id));
              onSendLocalMatches(picked);
              clearSelection();
            }}
            className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1.5 disabled:opacity-40"
            data-testid="dealer-links-send-local-matches-btn"
          >
            <Send size={12} />
            Send as Local Matches ({selectedIds.size})
          </Button>
        </div>
      )}

      {/* Existing links */}
      {links.length > 0 && (
        <div className="space-y-2">
          {links.map((link) => {
            const selected = selectedIds.has(link.id);
            return (
            <div
              key={link.id}
              className={`rounded-lg p-3 border transition-colors ${
                selected
                  ? (isDark ? 'bg-indigo-500/20 border-indigo-400' : 'bg-indigo-50 border-indigo-400')
                  : (isDark ? 'bg-gray-800/60 border-indigo-500/20' : 'bg-white border-indigo-100')
              }`}
              data-testid={`dealer-link-row-${link.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <label className="flex items-start gap-2 flex-1 min-w-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleSelect(link.id)}
                    className="mt-1 w-4 h-4 accent-indigo-600 cursor-pointer"
                    data-testid={`dealer-link-select-${link.id}`}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {link.title}
                    </p>
                    <a
                      href={link.cloaked_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className={`text-xs font-mono break-all hover:underline ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}
                      data-testid={`dealer-link-cloaked-${link.id}`}
                    >
                      {link.cloaked_url}
                    </a>
                    <p className={`text-[11px] mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <MousePointerClick size={11} className="inline mr-1" />
                      {link.clicks || 0} click{link.clicks === 1 ? '' : 's'}
                      {link.unread_click && (
                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">
                          NEW
                        </span>
                      )}
                    </p>
                  </div>
                </label>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(link.cloaked_url)}
                    className="h-7 w-7 p-0"
                    title="Copy cloaked URL"
                    data-testid={`dealer-link-copy-${link.id}`}
                  >
                    <Copy size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onSendInEmail(link)}
                    className="h-7 w-7 p-0 text-blue-600 dark:text-blue-400"
                    title="Send in email"
                    data-testid={`dealer-link-email-${link.id}`}
                  >
                    <Send size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(link.id)}
                    className="h-7 w-7 p-0 text-red-600 dark:text-red-400"
                    title="Delete link"
                    data-testid={`dealer-link-delete-${link.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const NotesSection = ({ notes, newNote, setNewNote, isAddingNote, setIsAddingNote, isSavingNote, onAddNote, onDeleteNote }) => {
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="bg-amber-50 dark:bg-amber-500/5 border border-amber-200 dark:border-amber-500/20 rounded-xl p-4" data-testid="notes-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wide flex items-center gap-2">
          <StickyNote size={16} />
          Notes
        </h3>
        {!isAddingNote && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsAddingNote(true)}
            className="flex items-center gap-1 h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-500/40 dark:text-amber-300 dark:hover:bg-amber-500/10 dark:bg-transparent"
            data-testid="add-note-btn"
          >
            <Plus size={12} />
            Add Note
          </Button>
        )}
      </div>

      {/* Add Note Form */}
      {isAddingNote && (
        <div className="mb-3 p-3 bg-white dark:bg-slate-900 rounded-lg border border-amber-200 dark:border-amber-500/30">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Enter your note..."
            className="w-full p-2 text-sm border border-gray-200 dark:border-gray-700 dark:bg-slate-800 dark:text-white rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
            rows={3}
            autoFocus
            data-testid="new-note-input"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setIsAddingNote(false); setNewNote(''); }}
              className="h-7 text-xs dark:border-gray-600 dark:bg-transparent dark:text-gray-200 dark:hover:bg-gray-800"
              disabled={isSavingNote}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={onAddNote}
              className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white"
              disabled={!newNote.trim() || isSavingNote}
              data-testid="save-note-btn"
            >
              {isSavingNote ? 'Saving...' : 'Save Note'}
            </Button>
          </div>
        </div>
      )}

      {/* Notes List */}
      {notes.length > 0 ? (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {notes.map((note) => (
            <div key={note.id} className="bg-white dark:bg-slate-800/60 rounded-lg p-3 border border-amber-100 dark:border-amber-500/20 group" data-testid={`note-${note.id}`}>
              <div className="flex justify-between items-start gap-2">
                <p className="text-sm text-gray-700 dark:text-gray-100 flex-1">{note.text}</p>
                <button
                  onClick={() => onDeleteNote(note.id)}
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-opacity p-1"
                  title="Delete note"
                  data-testid={`delete-note-${note.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{formatDate(note.created_at)}</p>
            </div>
          ))}
        </div>
      ) : (
        !isAddingNote && (
          <p className="text-sm text-amber-600 dark:text-amber-400 text-center py-2">No notes yet. Click &ldquo;Add Note&rdquo; to get started.</p>
        )
      )}
    </div>
  );
};

/**
 * Tracking Activity Section - Shows page views and click history
 */
const TrackingActivitySection = ({ lead }) => {
  const pageViews = lead.page_views || [];
  const clicks = lead.click_tracking || [];
  const totalActivity = pageViews.length + clicks.length;

  // Helper to format relative time
  const formatRelativeTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Combine and sort all activities by timestamp (most recent first)
  const allActivities = [
    ...pageViews.map(pv => ({ type: 'view', ...pv })),
    ...clicks.map(c => ({ type: 'click', ...c }))
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  if (totalActivity === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
        <h3 className="text-sm font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wide mb-3 flex items-center gap-2">
          <MousePointerClick size={16} />
          Tracking Activity
        </h3>
        <p className="text-sm text-gray-400 text-center py-4">No activity recorded yet</p>
      </div>
    );
  }

  return (
    <div className="bg-purple-50 dark:bg-gray-800 rounded-xl p-4" data-testid="tracking-activity-section">
      <h3 className="text-sm font-bold text-purple-800 uppercase tracking-wide mb-3 flex items-center gap-2">
        <MousePointerClick size={16} />
        Tracking Activity ({totalActivity} events)
      </h3>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-purple-100 text-center">
          <div className="flex items-center justify-center gap-2 text-purple-600 mb-1">
            <Eye size={16} />
            <span className="text-lg font-bold">{pageViews.length}</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-300">Page Views</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-purple-100 text-center">
          <div className="flex items-center justify-center gap-2 text-purple-600 mb-1">
            <MousePointerClick size={16} />
            <span className="text-lg font-bold">{clicks.length}</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-300">Link Clicks</p>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {allActivities.slice(0, 10).map((activity, index) => (
          <div key={index} className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-purple-100 flex items-start gap-3">
            <div className={`p-1.5 rounded-full ${activity.type === 'click' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
              {activity.type === 'click' ? <MousePointerClick size={14} /> : <Eye size={14} />}
            </div>
            <div className="flex-1 min-w-0">
              {activity.type === 'click' ? (
                <>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {activity.link_type === 'advisor_help' ? '🙋 Advisor help requested: ' : 'Searched online: '}
                    {activity.rv_year} {activity.rv_brand} {activity.rv_model}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-300">
                    {activity.link_type === 'advisor_help'
                      ? 'High intent — wants help finding this exact RV'
                      : `Source: ${activity.source === 'email' ? 'Email Link' : 'My Matches Page'}`}
                  </p>
                </>
              ) : (
                <p className="text-sm font-medium text-gray-800 dark:text-gray-100">Viewed My Matches page</p>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-400 whitespace-nowrap">
              <Clock size={12} />
              {formatRelativeTime(activity.timestamp)}
            </div>
          </div>
        ))}
        {allActivities.length > 10 && (
          <p className="text-xs text-center text-gray-400 py-2">
            +{allActivities.length - 10} more events
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Quiz Responses Section
 */
/**
 * Best-effort ZIP → City, State lookup via free zippopotam.us API. Cached
 * in localStorage per-ZIP so the same code never re-fetches.
 */
const useCityState = (zip) => {
  const [label, setLabel] = useState(null);
  useEffect(() => {
    if (!zip || !/^\d{5}$/.test(String(zip).trim())) return;
    const key = `zip_cache_${zip}`;
    try {
      const cached = localStorage.getItem(key);
      if (cached) { setLabel(cached); return; }
    } catch { /* ignore */ }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`https://api.zippopotam.us/us/${zip}`);
        if (!r.ok) return;
        const d = await r.json();
        const p = d?.places?.[0];
        if (!p) return;
        const val = `${p['place name']}, ${p['state abbreviation']}`;
        if (!cancelled) {
          setLabel(val);
          try { localStorage.setItem(key, val); } catch { /* ignore */ }
        }
      } catch { /* offline / rate-limited */ }
    })();
    return () => { cancelled = true; };
  }, [zip]);
  return label;
};

/**
 * LocationChip — shows "City, ST" when we can resolve the ZIP, falls back
 * to the raw ZIP otherwise. Zero-configuration public API — no key needed.
 */
const LocationChip = ({ zip }) => {
  const label = useCityState(zip);
  return <span>{label || zip}</span>;
};

/**
 * TowTradeCard — surfaces tow vehicle + trade-in info the buyer entered.
 * Only renders when at least one block has data.
 */
const TowTradeCard = ({ lead, isDark }) => {
  const tow = lead?.tow_vehicle || {};
  const trade = lead?.trade_info || lead?.trade || {};
  const hasTow = lead?.has_tow_vehicle || tow?.year || tow?.make || tow?.model;
  const hasTrade = lead?.has_trade || trade?.year || trade?.make || trade?.model;
  if (!hasTow && !hasTrade) return null;

  const towLine = [tow?.year, tow?.make, tow?.model, tow?.trim].filter(Boolean).join(' ').trim();
  const tradeLine = [trade?.year, trade?.make, trade?.model, trade?.trim].filter(Boolean).join(' ').trim();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="tow-trade-card">
      {hasTow && (
        <div className={`rounded-xl p-4 border ${isDark ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-emerald-50 border-emerald-100'}`}>
          <h3 className={`text-xs font-bold uppercase tracking-wide flex items-center gap-2 mb-2 ${isDark ? 'text-emerald-300' : 'text-emerald-800'}`}>
            <Truck size={14} />
            Tow Vehicle
          </h3>
          <p className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {towLine || 'Yes — details not provided'}
          </p>
          {tow?.vin && (
            <p className={`text-xs mt-1 font-mono ${isDark ? 'text-emerald-200' : 'text-emerald-700'}`}>VIN: {tow.vin}</p>
          )}
          {tow?.tow_capacity && (
            <p className={`text-xs mt-1 ${isDark ? 'text-emerald-200/80' : 'text-emerald-700/80'}`}>
              Max tow capacity: <strong>{tow.tow_capacity.toLocaleString?.() || tow.tow_capacity} lbs</strong>
            </p>
          )}
        </div>
      )}
      {hasTrade && (
        <div className={`rounded-xl p-4 border ${isDark ? 'bg-amber-500/5 border-amber-500/20' : 'bg-amber-50 border-amber-100'}`}>
          <h3 className={`text-xs font-bold uppercase tracking-wide flex items-center gap-2 mb-2 ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
            <DollarSign size={14} />
            Trade-In
          </h3>
          <p className={`text-base font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {tradeLine || 'Yes — details not provided'}
          </p>
          {trade?.value_estimate && (
            <p className={`text-xs mt-1 ${isDark ? 'text-amber-200/80' : 'text-amber-700/80'}`}>
              Estimated value: <strong>${Number(trade.value_estimate).toLocaleString()}</strong>
            </p>
          )}
          {trade?.notes && (
            <p className={`text-xs mt-2 whitespace-pre-wrap ${isDark ? 'text-amber-100/70' : 'text-amber-900/80'}`}>{trade.notes}</p>
          )}
        </div>
      )}
    </div>
  );
};


const QuizResponsesSection = ({ responses }) => {
  const [copied, setCopied] = useState(false);

  const copyAll = async () => {
    const text = Object.entries(responses)
      .map(([q, a]) => `${q}: ${a}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked */ }
  };

  return (
    <div className="bg-blue-50 dark:bg-blue-500/5 border border-transparent dark:border-blue-500/20 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="text-sm font-bold text-[#004E86] dark:text-blue-300 uppercase tracking-wide flex items-center gap-2">
          <MessageSquare size={16} />
          Form Responses ({Object.keys(responses).length} answers)
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={copyAll}
          className="h-7 text-xs flex items-center gap-1"
          data-testid="form-responses-copy-btn"
        >
          {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
          {copied ? 'Copied!' : 'Copy All'}
        </Button>
      </div>
      {/* Full grid — no scroll cap. Dealer sees every answer at once. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {Object.entries(responses).map(([question, answer], index) => (
          <div key={index} className="bg-white dark:bg-slate-800/60 rounded-lg p-3 border border-blue-100 dark:border-blue-500/20">
            <p className="text-[11px] font-semibold text-[#004E86]/70 dark:text-blue-300/80 uppercase tracking-wide mb-1">{question}</p>
            <p className="text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">{answer || '—'}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * AI-Extracted Matching Criteria Section
 */
const MatchingCriteriaSection = ({ criteria }) => (
  <div className="bg-amber-50 dark:bg-amber-500/5 border border-transparent dark:border-amber-500/20 rounded-xl p-4">
    <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-3">AI-Extracted Preferences</h3>
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
      <CriteriaCard label="Budget" value={`$${criteria.budget_max?.toLocaleString()}`} valueClass="text-green-600 dark:text-green-400" />
      <CriteriaCard label="Type" value={criteria.rv_type?.replace(/_/g, ' ')} valueClass="text-[#004E86] dark:text-blue-300 capitalize" />
      <CriteriaCard label="Sleeps" value={criteria.sleep_capacity || 'Any'} />
      <CriteriaCard label="Max Length" value={`${criteria.length_max || 'Any'}'`} />
      <CriteriaCard label="Off-Grid" value={criteria.off_grid ? 'Yes' : 'No'} />
      <CriteriaCard label="Style" value={criteria.camping_style || 'N/A'} valueClass="capitalize" />
    </div>
  </div>
);

const CriteriaCard = ({ label, value, valueClass = '' }) => (
  <div className="bg-white dark:bg-slate-800/60 rounded-lg p-2 text-center border border-amber-100 dark:border-amber-500/20">
    <p className="text-xs text-gray-500 dark:text-gray-300">{label}</p>
    <p className={`text-sm font-bold text-gray-900 dark:text-white ${valueClass}`}>{value}</p>
  </div>
);

/**
 * Matched RVs Section (without header - header is in parent with Generate button)
 */
const MatchedRVsSection = ({ matches, budgetMax }) => (
  <div className="grid gap-4">
    {matches.map((match, idx) => (
      <RVMatchCard key={idx} match={match} index={idx} budgetMax={budgetMax} />
    ))}
  </div>
);

/**
 * Individual RV Match Card
 */
const RVMatchCard = ({ match, index, budgetMax }) => {
  const rv = match.rv || {};
  const labelColor = match.label?.includes('Best Match') ? 'bg-green-500 text-white' :
                    match.label?.includes('Best Value') ? 'bg-blue-50 dark:bg-gray-8000 text-white' :
                    'bg-amber-50 dark:bg-gray-8000 text-white';
  const isWithinBudget = rv.price <= budgetMax;

  return (
    <div className="bg-white dark:bg-gray-900 border-2 border-gray-100 rounded-xl overflow-hidden hover:border-[#004E86] transition-colors">
      <div className="flex">
        {/* RV Image - Only show if there's a real image URL */}
        {rv.image_url && (
          <div className="w-48 h-40 flex-shrink-0 bg-gray-100">
            <img 
              src={rv.image_url} 
              alt={`${rv.year} ${rv.brand} ${rv.model}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Hide the entire image container if image fails to load
                e.target.parentElement.style.display = 'none';
              }}
            />
          </div>
        )}
        
        {/* RV Details */}
        <div className="flex-1 p-4">
          <div className="flex justify-between items-start mb-2">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-[#004E86] dark:text-blue-300">#{index + 1}</span>
                <span className={`text-xs px-2 py-1 rounded-full font-semibold ${labelColor}`}>
                  {match.label}
                </span>
                <span className="text-xs text-gray-400">Score: {match.score}/100</span>
              </div>
              <h4 className="text-lg font-bold mt-1">{rv.year} {rv.brand} {rv.model}</h4>
            </div>
            <div className="text-right">
              <p className={`text-2xl font-bold ${isWithinBudget ? 'text-green-600' : 'text-red-500'}`}>
                ${rv.price?.toLocaleString()}
              </p>
              {!isWithinBudget && (
                <p className="text-xs text-red-500">Over budget</p>
              )}
              {rv.msrp_range && (
                <p className="text-xs text-gray-400">{rv.msrp_range}</p>
              )}
            </div>
          </div>
          
          {/* Features & Specs */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="text-xs bg-gray-100 px-2 py-1 rounded">Sleeps {rv.sleeps}</span>
            <span className="text-xs bg-gray-100 px-2 py-1 rounded">{rv.length}' Length</span>
            <span className="text-xs bg-gray-100 px-2 py-1 rounded capitalize">{rv.type?.replace(/_/g, ' ')}</span>
            {rv.off_grid && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Off-Grid Ready</span>
            )}
          </div>
          
          {rv.features && rv.features.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {rv.features.slice(0, 4).map((feature, fidx) => (
                <span key={fidx} className="text-xs bg-blue-50 dark:bg-gray-800 text-blue-700 px-2 py-0.5 rounded">
                  {feature}
                </span>
              ))}
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <p className="text-xs text-gray-500 dark:text-gray-300 italic max-w-md truncate">
              {match.reasons?.[0]?.replace(/^[✓△✗•]\s*/, '') || 'Great match for your needs'}
            </p>
            <Button
              size="sm"
              onClick={() => window.open(match.google_search_link || `https://www.google.com/search?q=${rv.year}+${rv.brand}+${rv.model}`, '_blank')}
              className="bg-[#004E86] hover:bg-[#003d6b] text-white"
            >
              <ExternalLink size={14} className="mr-1" />
              Search Online
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * No Matches Message
 */
const NoMatchesSection = () => (
  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center">
    <Truck size={40} className="mx-auto text-gray-300 mb-2" />
    <p className="text-gray-500 dark:text-gray-300">No RV matches generated yet for this lead.</p>
  </div>
);

/**
 * Email Communication Section with compose + history
 */
const EmailSection = ({ lead, isDark, showCompose, setShowCompose, emailHistory, onEmailSent, preloadedTemplate }) => {
  const composerRef = useRef(null);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  // Auto-scroll to the composer when it opens (so clicks from the header
  // action bar or "Send Local Matches" button don't require scrolling to
  // find the compose form).
  useEffect(() => {
    if (showCompose && composerRef.current) {
      composerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [showCompose]);

  // Calculate stats
  const totalOpens = emailHistory.reduce((sum, e) => sum + (e.open_count || 0), 0);
  const totalClicks = emailHistory.reduce((sum, e) => sum + (e.results_click_count || 0), 0);
  const openedCount = emailHistory.filter(e => e.open_count > 0).length;

  return (
    <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-800' : 'bg-blue-50'}`} data-testid="email-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className={`text-sm font-bold uppercase tracking-wide flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-blue-800'}`}>
          <Mail size={16} /> Email Communication
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowCompose(!showCompose)}
          className="flex items-center gap-1 h-7 text-xs"
          data-testid="toggle-email-compose-btn"
        >
          {showCompose ? <X size={12} /> : <Send size={12} />}
          {showCompose ? 'Close' : 'Compose Email'}
        </Button>
      </div>

      {/* Compose Form */}
      {showCompose && (
        <div ref={composerRef} className={`mb-4 p-3 rounded-lg border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-blue-200'}`}>
          <EmailCompose
            leadId={lead.id}
            leadEmail={lead.email}
            leadName={lead.name}
            isDark={isDark}
            preloadedTemplate={preloadedTemplate}
            onSent={() => { setShowCompose(false); onEmailSent(); }}
          />
        </div>
      )}

      {/* Email Stats Summary */}
      {emailHistory.length > 0 && (
        <div className={`flex items-center gap-4 text-xs mb-3 pb-3 border-b ${isDark ? 'border-gray-700' : 'border-blue-200'}`}>
          <span className={isDark ? 'text-gray-400' : 'text-blue-700'}>
            {emailHistory.length} sent
          </span>
          <span className="text-green-600">
            <Eye size={12} className="inline mr-1" />{openedCount} opened
          </span>
          {totalClicks > 0 && (
            <span className="text-blue-600">
              <MousePointerClick size={12} className="inline mr-1" />{totalClicks} clicks
            </span>
          )}
        </div>
      )}

      {/* Email History */}
      {emailHistory.length > 0 ? (
        <div className="space-y-2">
          <p className={`text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-blue-700'}`}>
            Email History
          </p>
          {emailHistory.slice(0, 8).map((email, idx) => {
            const emailKey = email.id || idx;
            const viewable = email.body_html || email.body;
            const isOpen = expandedEmail === emailKey;
            return (
            <div key={emailKey}>
            <div
              onClick={() => viewable && setExpandedEmail(isOpen ? null : emailKey)}
              className={`flex items-center gap-3 p-2 rounded-lg text-sm ${isDark ? 'bg-gray-700/50' : 'bg-white'} ${viewable ? 'cursor-pointer hover:ring-1 hover:ring-blue-400' : ''}`}
              data-testid={`email-history-item-${emailKey}`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                email.open_count > 0 ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {email.open_count > 0 ? (
                  <MailOpen size={12} className="text-green-600" />
                ) : (
                  <CheckCircle2 size={12} className="text-gray-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  {email.subject || email.email_type || 'RV Matches Email'}
                </p>
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {email.email_type === 'drip' ? `Auto Drip · Day ${email.drip_day ?? ''}` : email.email_type === 'matches' ? 'Matches' : email.email_type || 'Auto'}
                  {email.open_count > 0 && ` • Opened ${email.open_count}x`}
                  {email.results_click_count > 0 && ` • ${email.results_click_count} clicks`}
                  {viewable && (isOpen ? ' • click to close' : ' • click to view')}
                </p>
              </div>
              <span className={`text-xs shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                {formatDate(email.sent_at)}
              </span>
            </div>
            {isOpen && viewable && (
              <div
                className={`mt-1 mb-2 p-3 rounded-lg border text-sm max-h-80 overflow-y-auto ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-800'}`}
                data-testid={`email-body-${emailKey}`}
              >
                {email.body_html
                  ? <div dangerouslySetInnerHTML={{ __html: email.body_html }} />
                  : <pre className="whitespace-pre-wrap font-sans text-xs">{email.body}</pre>}
              </div>
            )}
            </div>
          ); })}
          {emailHistory.length > 8 && (
            <p className={`text-xs text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              + {emailHistory.length - 8} more emails
            </p>
          )}
        </div>
      ) : (
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-blue-600/60'}`}>
          No emails sent to this lead yet.
        </p>
      )}
    </div>
  );
};

/**
 * LeadAttachmentsSection — shows files the customer uploaded on the
 * application form. Dealer can click any file to open / download it.
 * URLs are unguessable UUIDs so no auth header is needed for download
 * (and the dealer can right-click → save).
 */
const LeadAttachmentsSection = ({ attachments, sessionId }) => {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const formatSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / 1024 / 1024;
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    return `${Math.round(bytes / 1024)} KB`;
  };
  return (
    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-2xl p-5 border-2 border-cyan-200 dark:border-cyan-700/50">
      <h3 className="text-sm font-bold text-cyan-900 dark:text-cyan-200 mb-3 uppercase tracking-wide flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
        Customer Attachments ({attachments.length})
      </h3>
      <ul className="space-y-2" data-testid="lead-attachments-list">
        {attachments.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/70 dark:bg-slate-900/40 border border-cyan-200/60 dark:border-cyan-700/40">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="text-xl shrink-0" aria-hidden="true">
                {a.mime?.startsWith('image/') ? '🖼️'
                 : (a.mime?.includes('pdf') ? '📄'
                 : (a.mime?.includes('sheet') || a.ext === '.csv' || a.ext === '.xlsx' ? '📊'
                 : '📎'))}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{a.original_name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{formatSize(a.size)}</p>
              </div>
            </div>
            <a
              href={`${API}/leads/attachment-file/${sessionId}/${a.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-cyan-600 hover:bg-cyan-700 text-white transition-colors"
              data-testid={`download-attachment-${a.id}`}
            >
              Open
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LeadDetailModal;
